import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useAuth } from "./AuthContext";
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification as apiDeleteNotification,
} from "../api/notifications";

const NotificationsContext = createContext(null);

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within NotificationsProvider",
    );
  }
  return context;
}

export const NOTIFICATION_TYPES = {
  NEW_REQUEST:        "new_request",
  APPROVED:           "approval",
  REJECTED:           "rejection",
  ESCALATED:          "escalated",
  FORWARDED:          "forwarded",
  APPEAL_RESPONSE:    "appeal_response",
  MODIFIED:           "modified",
  COMPLAINT_NEW:      "complaint_new",
  COMPLAINT_RESPONSE: "complaint_response",
  RESIGNATION_NEW:    "resignation_new",
  RESIGNATION_RESPONSE: "resignation_response",
  EMPLOYEE_HIRED:     "employee_hired",
  EMPLOYEE_TERMINATED: "employee_terminated",
};

// kept for legacy callers that still build notifications locally
export function createNotification({
  recipientId,
  recipientRole,
  type,
  title,
  message,
  relatedRequestId,
  senderName,
  senderRole,
  requestType,
  requestStatus,
}) {
  return {
    id: Date.now().toString(36) + Math.random().toString(36).slice(2),
    recipientId,
    recipientRole,
    type,
    title,
    message,
    relatedRequestId,
    senderName,
    senderRole,
    requestType,
    requestStatus,
    isRead: false,
    read: false,
    createdAt: new Date().toISOString(),
  };
}

const POLL_INTERVAL_MS = 30_000; // every 30 seconds

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]             = useState(false);
  const timerRef                          = useRef(null);
  const snapshotRef                       = useRef("");

  // normalise a backend row so it has both .isRead and .read
  // and recipientId for legacy UI filters.
  function normalise(n) {
    return {
      ...n,
      read: n.isRead ?? n.read ?? false,
      recipientId: n.recipientId ?? n.userId ?? null,
    };
  }

  const loadNotifications = useCallback(async () => {
    if (!user?.id) return;
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      return;
    }
    try {
      const data = await fetchNotifications({ limit: 50 });
      if (Array.isArray(data)) {
        const next = data.map(normalise);
        const snap = next.map((row) => `${row.id}:${row.read ? 1 : 0}`).join("|");
        if (snap === snapshotRef.current) return;
        snapshotRef.current = snap;
        setNotifications(next);
      }
    } catch {
      // silent — keep showing cached list
    }
  }, [user?.id]);

  // initial load + polling
  useEffect(() => {
    if (!user?.id || user?.role === "platform-admin") {
      snapshotRef.current = "";
      setNotifications([]);
      return;
    }
    setLoading(true);
    loadNotifications().finally(() => setLoading(false));

    timerRef.current = setInterval(loadNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [user?.id, user?.role, loadNotifications]);

  // addNotification — for session-only notifications still created on the frontend
  const addNotification = useCallback(
    (notification) => {
      if (!user) return;
      setNotifications((prev) => [normalise(notification), ...prev]);
    },
    [user],
  );

  // kept as no-op for legacy callers
  const addNotificationToUser = useCallback(() => {}, []);

  const markAsRead = useCallback(
    async (notificationId) => {
      const id = String(notificationId);
      setNotifications((prev) =>
        prev.map((n) =>
          String(n.id) === id ? { ...n, isRead: true, read: true } : n,
        ),
      );
      try {
        await markNotificationRead(notificationId);
      } catch {
        // revert on failure
        await loadNotifications();
      }
    },
    [loadNotifications],
  );

  const markAllAsRead = useCallback(async () => {
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, isRead: true, read: true })),
    );
    try {
      await markAllNotificationsRead();
    } catch {
      await loadNotifications();
    }
  }, [loadNotifications]);

  const deleteNotification = useCallback(
    async (notificationId) => {
      setNotifications((prev) =>
        prev.filter((n) => n.id !== notificationId),
      );
      try {
        await apiDeleteNotification(notificationId);
      } catch {
        await loadNotifications();
      }
    },
    [loadNotifications],
  );

  const unreadCount = notifications.filter(
    (n) => !(n.isRead ?? n.read),
  ).length;

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        addNotification,
        addNotificationToUser,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        refreshNotifications: loadNotifications,
        NOTIFICATION_TYPES,
        createNotification,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}
